import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useState } from "react";
import ReactDOMServer from "react-dom/server";
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Switch,
  TextField,
  IconButton,
  Tooltip,
  MenuItem
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { collection, doc, getDocs, updateDoc } from "firebase/firestore";
import { db } from "../Config/firebase";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import RelatorioCalibracaoPrensaPrint from "./RelatorioCalibracaoPrensaPrint";
import { abrirJanelaImpressao } from "./impressaoCalibracaoPrensa";
import { consultaTenantAtual } from "../Utils/tenant";

export default function ListaCalibracoesPrensa() {
  const [calibracoes, setCalibracoes] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [modoPreview, setModoPreview] = useState(false);
  const [calibracaoPreview, setCalibracaoPreview] = useState(null);

  const history = useHistory();

  useEffect(() => {
    carregarCalibracoes();
  }, []);

  async function carregarCalibracoes() {
    const snap = await getDocs(consultaTenantAtual(collection(db, "tblCalibracaoPrensas")));

    const lista = snap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      dataVencimento:
        doc.data().dataVencimento ||
        calcularDataVencimentoISO(doc.data().dataCalibracao || doc.data().data, doc.data().validade)
    })).sort((a, b) => {
      const numeroA = extrairNumeroSequencialCodigo(a.codigo);
      const numeroB = extrairNumeroSequencialCodigo(b.codigo);

      if (numeroA !== numeroB) {
        return numeroB - numeroA;
      }

      return normalizarDataISO(b.dataCalibracao).localeCompare(normalizarDataISO(a.dataCalibracao));
    });

    setCalibracoes(lista);
  }

  async function alterarStatusCertificado(certificado, ativo) {
    try {
      await updateDoc(doc(db, "tblCalibracaoPrensas", certificado.id), {
        ativo,
      });
      setCalibracoes((atuais) =>
        atuais.map((item) =>
          item.id === certificado.id ? { ...item, ativo } : item
        )
      );
      window.dispatchEvent(
        new CustomEvent("calibracoes-prensa-atualizadas", {
          detail: { id: certificado.id, ativo },
        })
      );
    } catch (erro) {
      console.error("Erro ao alterar status do certificado:", erro);
      alert("Não foi possível alterar o status do certificado.");
    }
  }

  function abrirCadastro() {
    history.push("/app/calibracao-prensa/novo");
  }

  function abrirEdicao(id) {
    history.push(`/app/calibracao-prensa/${id}`);
  }

  function formatarDataBR(data) {
    if (!data) return "-";

    if (data.includes("-")) {
      const [ano, mes, dia] = data.split("-");
      return `${dia}/${mes}/${ano}`;
    }

    return data;
  }

  function normalizarDataISO(data) {
    if (!data) return "";

    if (typeof data === "string" && /^\d{4}-\d{2}-\d{2}$/.test(data)) {
      return data;
    }

    if (typeof data === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(data)) {
      const [dia, mes, ano] = data.split("/");
      return `${ano}-${mes}-${dia}`;
    }

    if (data?.toDate) {
      return normalizarDataISO(data.toDate());
    }

    if (data instanceof Date && !Number.isNaN(data.getTime())) {
      const ano = data.getFullYear();
      const mes = String(data.getMonth() + 1).padStart(2, "0");
      const dia = String(data.getDate()).padStart(2, "0");
      return `${ano}-${mes}-${dia}`;
    }

    return "";
  }

  function calcularDataVencimentoISO(dataCalibracao, validade) {
    const dataBase = normalizarDataISO(dataCalibracao);
    const textoValidade = String(validade || "").trim().toLowerCase().replace(",", ".");

    if (!dataBase || !textoValidade) {
      return "";
    }

    const quantidade = Number(textoValidade.match(/[\d.]+/)?.[0]);

    if (!Number.isFinite(quantidade) || quantidade <= 0) {
      return "";
    }

    const [ano, mes, dia] = dataBase.split("-").map(Number);
    const vencimento = new Date(ano, mes - 1, dia);

    if (textoValidade.includes("mes")) {
      vencimento.setMonth(vencimento.getMonth() + quantidade);
    } else {
      vencimento.setFullYear(vencimento.getFullYear() + quantidade);
    }

    return normalizarDataISO(vencimento);
  }

  function obterStatusVencimento(dataVencimento) {
    const dataISO = normalizarDataISO(dataVencimento);

    if (!dataISO) {
      return { cor: "text.secondary", texto: "-" };
    }

    const [ano, mes, dia] = dataISO.split("-").map(Number);
    const hoje = new Date();
    const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    const vencimento = new Date(ano, mes - 1, dia);
    const diasRestantes = Math.ceil((vencimento.getTime() - inicioHoje.getTime()) / 86400000);

    if (diasRestantes < 0) {
      return { cor: "error.main", texto: formatarDataBR(dataISO) };
    }

    if (diasRestantes <= 30) {
      return { cor: "warning.main", texto: formatarDataBR(dataISO) };
    }

    return { cor: "success.main", texto: formatarDataBR(dataISO) };
  }

  function obterChaveStatusVencimento(dataVencimento) {
    const dataISO = normalizarDataISO(dataVencimento);

    if (!dataISO) {
      return "sem-data";
    }

    const [ano, mes, dia] = dataISO.split("-").map(Number);
    const hoje = new Date();
    const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    const vencimento = new Date(ano, mes - 1, dia);
    const diasRestantes = Math.ceil((vencimento.getTime() - inicioHoje.getTime()) / 86400000);

    if (diasRestantes < 0) {
      return "vencido";
    }

    if (diasRestantes <= 30) {
      return "faltando-30-dias";
    }

    return "em-dia";
  }

  function formatarCodigoCalibracao(codigo, dataCalibracao) {
    const texto = String(codigo || "").trim();
    if (!texto) return "-";

    if (/^RE-CAL\s*-\s*\d+\/\d{4}$/i.test(texto)) {
      return texto;
    }

    const grupos = texto.match(/\d+/g);
    const numero = grupos?.length ? grupos.sort((a, b) => b.length - a.length)[0] : null;
    const ano =
      typeof dataCalibracao === "string" && /^\d{4}/.test(dataCalibracao)
        ? dataCalibracao.slice(0, 4)
        : String(new Date().getFullYear());

    if (!numero) return texto;

    return `RE-CAL - ${numero.padStart(4, "0")}/${ano}`;
  }

  function extrairNumeroSequencialCodigo(codigo) {
    const texto = String(codigo || "");
    const codigoFormatado = texto.match(/RE-CAL\s*-\s*(\d+)\s*\/\s*\d{4}/i);
    const numero = Number(codigoFormatado?.[1] || texto.match(/\d+/)?.[0] || 0);

    return Number.isFinite(numero) ? numero : 0;
  }

  const listaFiltrada = calibracoes.filter((c) => {
    const correspondeTexto =
      contemTextoPesquisa(String(c.empresa ?? "")
        .toLowerCase(), filtro.toLowerCase()) ||
      contemTextoPesquisa(String(c.obra ?? "")
        .toLowerCase(), filtro.toLowerCase()) ||
      contemTextoPesquisa(String(c.serie ?? "")
        .toLowerCase(), filtro.toLowerCase()) ||
      contemTextoPesquisa(String(c.codigo ?? "")
        .toLowerCase(), filtro.toLowerCase());

    if (!correspondeTexto) {
      return false;
    }

    if (filtroStatus === "todos") {
      return true;
    }

    return obterChaveStatusVencimento(c.dataVencimento) === filtroStatus;
  });

  function imprimir(dados) {
    const html = ReactDOMServer.renderToStaticMarkup(
      <RelatorioCalibracaoPrensaPrint dados={dados} />
    );

    abrirJanelaImpressao({
      title: "Certificado de Calibracao de Prensa",
      html,
      printStyles: "@page { size: A4 portrait; margin: 5mm; }"
    });
  }

  function visualizar(dados) {
    setCalibracaoPreview(dados);
    setModoPreview(true);
  }

  if (modoPreview && calibracaoPreview) {
    return (
      <RelatorioCalibracaoPrensaPrint
        dados={calibracaoPreview}
        onVoltar={() => {
          setModoPreview(false);
          setCalibracaoPreview(null);
        }}
      />
    );
  }

  return (
    <>
      <Navbar />

      <Box sx={{ p: 4 }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>Calibração de Prensas</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Lista de Calibrações Cadastradas</Typography>
        <Stack direction="row" spacing={2} mb={3}>
          <Button variant="contained" onClick={abrirCadastro}>
            + Nova Calibração
          </Button>

          <TextField
            size="small"
            label="Pesquisar"
            placeholder="Cliente, obra ou série"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="vencido">Vencido</MenuItem>
            <MenuItem value="faltando-30-dias">Faltando 30 dias</MenuItem>
          </TextField>
        </Stack>
        </Paper>

        <Paper>
          <Table className="lista-cadastro-padrao" size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">
                  <strong>Código</strong>
                </TableCell>

                <TableCell>Cliente / Obra</TableCell>

                <TableCell>Equipamento</TableCell>

                <TableCell>Série</TableCell>

                <TableCell>Data Calibração</TableCell>

                <TableCell align="center">Status</TableCell>

                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {listaFiltrada.map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  style={{ cursor: "pointer" }}
                  onClick={() => abrirEdicao(c.id)}
                >
                  <TableCell align="center">
                    {formatarCodigoCalibracao(c.codigo, c.dataCalibracao)}
                  </TableCell>

                  <TableCell>
                    <div className="lista-dado-principal">{c.empresa || "-"}</div>
                    <div className="lista-dado-secundario">Obra: {c.obra || "-"}</div>
                  </TableCell>

                  <TableCell className="lista-dado-destaque">{c.equipamento}</TableCell>

                  <TableCell className="lista-dado-secundario">{c.serie}</TableCell>

                  <TableCell className="lista-dado-secundario">
                    <Typography variant="body2">
                      {formatarDataBR(c.dataCalibracao)}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: obterStatusVencimento(c.dataVencimento).cor,
                        fontWeight: 700
                      }}
                    >
                      Vence em {obterStatusVencimento(c.dataVencimento).texto}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Stack
                      alignItems="center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Switch
                        size="small"
                        checked={c.ativo !== false}
                        onChange={(e) =>
                          alterarStatusCertificado(c, e.target.checked)
                        }
                        inputProps={{
                          "aria-label": `Certificado ${
                            c.ativo !== false ? "ativo" : "inativo"
                          }`,
                        }}
                      />
                      <Typography
                        variant="caption"
                        color={c.ativo !== false ? "success.main" : "text.secondary"}
                        fontWeight={700}
                      >
                        {c.ativo !== false ? "Ativo" : "Inativo"}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="Visualizar">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            visualizar(c);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirEdicao(c.id);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Clonar certificado">
                        <IconButton size="small" aria-label="Clonar certificado" onClick={(event) => {
                          event.stopPropagation();
                          history.push(`/app/calibracao-prensa/novo?clonar=${encodeURIComponent(c.id)}`);
                        }}>
                          <ContentCopyIcon sx={{ color: "#0288d1" }} fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <HistoricoCadastro colecao="tblCalibracaoPrensas" documentoId={c.id} registro={c} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </>
  );
}
